'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Menu, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { getAccessToken, getRefreshToken, getUser, clearAuth, AUTH_CHANGE_EVENT } from '../utils/storage';
import type { User } from '@/shared/types';
import { api } from '@/lib/api';
import { API } from '../config/env';
import ThemeToggle from '@/components/ThemeToggle';

function BrandMark() {
  return (
    <Image src="/logo-icon.svg" alt="DiecastShowcase logo" width={36} height={36} className="h-9 w-9" priority />
  );
}

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const isMountedRef = useRef(true);

  useEffect(() => {
    const token = getAccessToken();
    const userData = getUser();

    if (token && userData) {
      setUser(userData);
    }
    setLoading(false);

    const handleAuthChange = () => {
      if (!isMountedRef.current) return;

      const nextToken = getAccessToken();
      const nextUserData = getUser();
      setUser(nextToken && nextUserData ? nextUserData : null);
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener(AUTH_CHANGE_EVENT, handleAuthChange);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener(AUTH_CHANGE_EVENT, handleAuthChange);
    };
  }, []);

  const handleLogout = () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      api.post(API.LOGOUT, { refreshToken }).catch(() => {});
    }

    clearAuth();
    setUser(null);
    setIsMobileMenuOpen(false);
    router.push('/');
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link href="/" className="flex items-center gap-3" onClick={() => setIsMobileMenuOpen(false)}>
          <BrandMark />
          <span className="text-lg font-extrabold tracking-tight text-foreground sm:text-xl">
            DiecastShowcase
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          <Link href="/explore" className="text-sm font-medium text-muted-foreground transition hover:text-foreground">
            Explore
          </Link>
          <Link href="/catalog/search" className="text-sm font-medium text-muted-foreground transition hover:text-foreground">
            Catalog
          </Link>
          <Link href="/brands" className="text-sm font-medium text-muted-foreground transition hover:text-foreground">
            Brands
          </Link>
          {user && (
            <Link href="/dashboard" className="text-sm font-medium text-muted-foreground transition hover:text-foreground">
              Dashboard
            </Link>
          )}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <ThemeToggle />
          {!loading && (
            <>
              {user ? (
                <>
                  <Link href="/settings" className="btn-ghost h-10 px-4">
                    Settings
                  </Link>
                  <button type="button" onClick={handleLogout} className="btn-ghost h-10 px-4">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link href="/login" className="btn-ghost h-10 px-4">
                    Sign In
                  </Link>
                  <Link href="/signup" className="btn-primary h-10 px-4">
                    Get Started
                  </Link>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card text-foreground"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="border-t border-border bg-card md:hidden">
          <div className="space-y-1 px-4 py-3">
            <Link href="/explore" className="block rounded-md px-3 py-2 text-sm font-medium text-foreground" onClick={() => setIsMobileMenuOpen(false)}>
              Explore
            </Link>
            <Link href="/catalog/search" className="block rounded-md px-3 py-2 text-sm font-medium text-foreground" onClick={() => setIsMobileMenuOpen(false)}>
              Catalog
            </Link>
            <Link href="/brands" className="block rounded-md px-3 py-2 text-sm font-medium text-foreground" onClick={() => setIsMobileMenuOpen(false)}>
              Brands
            </Link>
            <Link href="/about" className="block rounded-md px-3 py-2 text-sm font-medium text-foreground" onClick={() => setIsMobileMenuOpen(false)}>
              About
            </Link>
            {user && (
              <Link href="/dashboard" className="block rounded-md px-3 py-2 text-sm font-medium text-foreground" onClick={() => setIsMobileMenuOpen(false)}>
                Dashboard
              </Link>
            )}

            <div className="mt-3 border-t border-border pt-3">
              {!loading && (
                <>
                  {user ? (
                    <div className="space-y-2">
                      <Link href="/settings" className="btn-ghost w-full justify-center" onClick={() => setIsMobileMenuOpen(false)}>
                        Settings
                      </Link>
                      <button type="button" onClick={handleLogout} className="btn-ghost w-full justify-center">
                        Logout
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link href="/login" className="btn-ghost w-full justify-center" onClick={() => setIsMobileMenuOpen(false)}>
                        Sign In
                      </Link>
                      <Link href="/signup" className="btn-primary w-full justify-center" onClick={() => setIsMobileMenuOpen(false)}>
                        Get Started
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

